import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Edit, Shield, User as UserIcon, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Villa við að sækja notendur');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'employee'
    });
    setEditMode(false);
    setCurrentUser(null);
  };

  const handleEdit = (userData) => {
    setEditMode(true);
    setCurrentUser(userData);
    setFormData({
      email: userData.email,
      password: '', // Don't show password
      full_name: userData.full_name,
      role: userData.role
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editMode && currentUser) {
        // Update user
        const updateData = {
          full_name: formData.full_name,
          role: formData.role
        };
        
        // Only include password if it's set
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axios.put(`${API}/users/${currentUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notandi uppfærður!');
      } else {
        // Create new user
        await axios.post(`${API}/auth/register`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notandi búinn til!');
      }
      
      setOpen(false);
      fetchUsers();
      resetForm();
    } catch (error) {
      console.error('Submit error:', error);
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Villa í gögnum');
        }
      } else {
        toast.error('Villa kom upp');
      }
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="user-management-page">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Work Sans' }}>Notendaumsjón</h1>
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-500 to-purple-600" data-testid="add-user-button">
                <UserPlus className="mr-2 h-4 w-4" />
                Nýr notandi
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editMode ? 'Breyta notanda' : 'Nýr notandi'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Fullt nafn</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="Jón Jónsson"
                    data-testid="user-fullname-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Netfang</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={editMode}
                    className={editMode ? 'bg-gray-100' : ''}
                    placeholder="jon@example.is"
                    data-testid="user-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Lykilorð {editMode && '(skildu eftir tómt til að halda núverandi)'}</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editMode}
                    placeholder="••••••••"
                    data-testid="user-password-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Hlutverk</Label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="user-role-select"
                  >
                    <option value="employee">Notandi (sér bara eigin tíma)</option>
                    <option value="admin">Stjórnandi (sér allt)</option>
                  </select>
                </div>
                
                <Button type="submit" className="w-full" data-testid="submit-user-button">
                  {editMode ? 'Uppfæra' : 'Búa til'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {users.map((userData) => (
            <Card key={userData.id} className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-full ${userData.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {userData.role === 'admin' ? (
                        <Shield className="h-6 w-6 text-purple-600" />
                      ) : (
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-xl">{userData.full_name}</CardTitle>
                      <p className="text-sm text-gray-600">{userData.email}</p>
                      <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-semibold ${
                        userData.role === 'admin' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {userData.role === 'admin' ? 'Stjórnandi' : 'Notandi'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(userData)}
                      variant="outline"
                      size="sm"
                      data-testid={`edit-user-${userData.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Breyta
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!window.confirm('Ertu viss um að vilja eyða þessum notanda?')) return;
                        try {
                          const token = localStorage.getItem('token');
                          await axios.delete(`${API}/users/${userData.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Notandi eyddur');
                          fetchUsers();
                        } catch (error) {
                          console.error('Delete user error:', error);
                          toast.error('Tókst ekki að eyða notanda');
                        }
                      }}
                      variant="destructive"
                      size="sm"
                      data-testid={`delete-user-${userData.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Eyða
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Notanda ID</p>
                    <p className="font-mono text-xs">{userData.id.slice(0, 8)}...</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Búið til</p>
                    <p className="font-medium">
                      {new Date(userData.created_at).toLocaleDateString('is-IS')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Réttindi</p>
                    <p className="font-medium">
                      {userData.role === 'admin' ? 'Sér allt í kerfinu' : 'Sér bara eigin tíma'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
